import React, { useEffect, useState } from 'react';
import { LayoutGroup, motion } from 'motion/react';
import RotatingText from '../RotatingText';

const API = 'https://yurinkov.dev/msg.php';
const BUCKET_COUNT = 12;
const dateFormatter = typeof Intl !== 'undefined'
  ? new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' })
  : null;
const timeFormatter = typeof Intl !== 'undefined'
  ? new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })
  : null;

const formatTimelineLabel = timestamp => {
  if (!Number.isFinite(timestamp)) return null;
  const date = new Date(timestamp);
  return {
    primary: dateFormatter ? dateFormatter.format(date) : date.toLocaleDateString(),
    secondary: timeFormatter ? timeFormatter.format(date) : date.toLocaleTimeString(),
    full: date.toLocaleString('ru-RU')
  };
};

const fallbackTimelineLabel = idx => ({
  primary: `Сегм. ${idx + 1}`,
  secondary: '',
  full: `Сегмент ${idx + 1}`
});

const parseTimestampValue = value => {
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) return numeric;
    const isoCandidate = value.includes(' ')
      ? value.replace(' ', 'T')
      : value;
    let parsedDate = Date.parse(isoCandidate);
    if (Number.isNaN(parsedDate)) {
      const mysqlMatch = value.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})$/);
      if (mysqlMatch) {
        parsedDate = Date.parse(`${mysqlMatch[1]}T${mysqlMatch[2]}Z`);
      }
    }
    return Number.isNaN(parsedDate) ? null : parsedDate;
  }
  return null;
};


function ChatPage() {
  const [wordCounts, setWordCounts] = useState({});
  const [wordInsights, setWordInsights] = useState({});
  const [userCounts, setUserCounts] = useState({});
  const [topWords, setTopWords] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedWord, setSelectedWord] = useState('');
  const [timelineLabels, setTimelineLabels] = useState(() =>
    Array.from({ length: BUCKET_COUNT }, (_, idx) => fallbackTimelineLabel(idx))
  );
  const totalMessages = Object.values(userCounts).reduce((sum, count) => sum + count, 0);
  const uniqueWords = Object.keys(wordCounts).length;
  const totalUsers = Object.keys(userCounts).length;
  const trendingWords = topWords.slice(0, 5);
  const selectedWordData = selectedWord ? wordInsights[selectedWord] : null;
  const selectedWordTotal = selectedWordData?.total || 0;
  const contributorBase = selectedWordTotal || 1;
  const topContributors = selectedWordData
    ? Object.entries(selectedWordData.byUser || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
    : [];
  const topContributor = topContributors[0];
  const bucketMax = selectedWordData?.buckets?.length
    ? Math.max(...selectedWordData.buckets, 1)
    : 1;
  const chartBuckets = selectedWordData?.buckets || [];
  const chartHasData = chartBuckets.length > 0;

  useEffect(() => {
    let mounted = true;
    fetch(API)
      .then(r => r.json())
      .then(payload => {
        if (!mounted) return;
        const messages = payload.messages || [];
        const wc = {};
        const uc = {};
        const bucketCount = BUCKET_COUNT;
        const totalMessagesCount = messages.length || 1;
        const wordMap = {};
        let minTimestamp = null;
        let maxTimestamp = null;

        messages.forEach(msg => {
          const ts = parseTimestampValue(
            msg?.timestamp ?? msg?.timestamp_ms ?? msg?.streamTime ?? msg?.streamDate
          );
          if (Number.isFinite(ts)) {
            if (minTimestamp === null || ts < minTimestamp) minTimestamp = ts;
            if (maxTimestamp === null || ts > maxTimestamp) maxTimestamp = ts;
          }
        });

        const hasTimestampWindow = minTimestamp !== null && maxTimestamp !== null;
        const bucketDuration = hasTimestampWindow && maxTimestamp !== minTimestamp
          ? Math.max((maxTimestamp - minTimestamp) / bucketCount, 1)
          : null;

        const computedLabels = hasTimestampWindow
          ? Array.from({ length: bucketCount }, (_, idx) => {
              if (!bucketDuration) {
                return formatTimelineLabel(minTimestamp) || fallbackTimelineLabel(idx);
              }
              return (
                formatTimelineLabel(minTimestamp + bucketDuration * idx) || fallbackTimelineLabel(idx)
              );
            })
          : Array.from({ length: bucketCount }, (_, idx) => fallbackTimelineLabel(idx));

        setTimelineLabels(computedLabels);

        messages.forEach((msg, idx) => {
          const u = msg.user || 'неизвестно';
          uc[u] = (uc[u] || 0) + 1;
          if (!msg.text) return;
          const ts = parseTimestampValue(
            msg?.timestamp ?? msg?.timestamp_ms ?? msg?.streamTime ?? msg?.streamDate
          );
          const bucketIndex = bucketDuration && Number.isFinite(ts) && minTimestamp !== null
            ? Math.min(
                bucketCount - 1,
                Math.floor(Math.max(0, ts - minTimestamp) / bucketDuration)
              )
            : Math.min(
                bucketCount - 1,
                Math.floor((idx / totalMessagesCount) * bucketCount)
              );
          msg.text
            .toLowerCase()
            .replace(/[.,!?()\"':;@#^&*<>~`\[\]{}\\/0-9]/g, ' ')
            .split(/\s+/)
            .filter(w => w.length > 2)
            .forEach(w => {
              wc[w] = (wc[w] || 0) + 1;
              if (!wordMap[w]) {
                wordMap[w] = {
                  total: 0,
                  buckets: Array(bucketCount).fill(0),
                  byUser: {}
                };
              }
              const insight = wordMap[w];
              insight.total += 1;
              insight.buckets[bucketIndex] += 1;
              insight.byUser[u] = (insight.byUser[u] || 0) + 1;
            });
        });
        setWordCounts(wc);
        setWordInsights(wordMap);
        setUserCounts(uc);
        setTopWords(Object.entries(wc).sort((a,b)=>b[1]-a[1]).slice(0,100));
        setTopUsers(Object.entries(uc).sort((a,b)=>b[1]-a[1]).slice(0,15));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const handleWordClick = rawWord => {
    if (!rawWord) return;
    const normalized = rawWord.toLowerCase().trim();
    if (!normalized || !wordInsights[normalized]) return;
    setSelectedWord(normalized);
  };

  const closeWordModal = () => setSelectedWord('');
  const searchHasData = query && wordCounts[query];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-white">
      <style>
        {`.hide-scrollbar::-webkit-scrollbar{display:none;} .hide-scrollbar{scrollbar-width:none;}`}
      </style>
      <LayoutGroup>
        <motion.p className="flex items-center gap-2 text-6xl font-extrabold tracking-tight mt-8 md:mt-10 lg:mt-12 mb-12 md:mb-16 lg:mb-20 z-20" layout>
          <motion.span layout transition={{ type: 'spring', damping: 30, stiffness: 400 }} className="">
            PESPATRON44
          </motion.span>
          <RotatingText
        texts={[
  'rus',
  'наш',
  'zzz',
  'дракон',
  'круТАЯ',
  'страпон'
  ]}
             mainClassName="px-3 sm:px-3 md:px-4 overflow-hidden py-1 sm:py-1 md:py-2 justify-center rounded-lg bg-gradient-to-r from-indigo-500/75 via-purple-500/75 to-pink-500/75 text-white shadow-lg"
     staggerFrom={"last"}
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "-120%" }}
      staggerDuration={0.025}
      splitLevelClassName="overflow-hidden pb-0.5 sm:pb-1 md:pb-1"
      transition={{ type: "spring", damping: 30, stiffness: 400 }}
      rotationInterval={2000}
    />
  </motion.p>
</LayoutGroup>

      <div className="w-full max-w-6xl space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="bg-gradient-to-br from-indigo-600/30 to-purple-600/20 border border-white/10 rounded-2xl p-4 text-left shadow-lg">
            <p className="text-sm uppercase tracking-wide text-indigo-200 mb-1">Всего сообщений</p>
            <p className="text-3xl font-bold text-white">{totalMessages.toLocaleString('ru-RU')}</p>
            <p className="text-xs text-white/70 mt-1">Счётчик обновляется в реальном времени</p>
          </div>
          <div className="bg-gradient-to-br from-pink-600/30 to-orange-500/20 border border-white/10 rounded-2xl p-4 text-left shadow-lg">
            <p className="text-sm uppercase tracking-wide text-pink-100 mb-1">Уникальные слова</p>
            <p className="text-3xl font-bold text-white">{uniqueWords.toLocaleString('ru-RU')}</p>
            <p className="text-xs text-white/70 mt-1">Фильтрация по словам длиннее 3 символов</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-600/30 to-cyan-500/20 border border-white/10 rounded-2xl p-4 text-left shadow-lg">
            <p className="text-sm uppercase tracking-wide text-emerald-100 mb-1">Участники</p>
            <p className="text-3xl font-bold text-white">{totalUsers.toLocaleString('ru-RU')}</p>
            <p className="text-xs text-white/70 mt-1">Считаем всех авторов сообщений</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_1.4fr_1fr]">
          <div className="bg-gradient-to-br from-black/60 to-black/40 border border-white/10 rounded-3xl p-6 shadow-2xl text-left">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">🔍 Поиск слова</h3>
              {trendingWords.length > 0 && (
                <span className="text-xs text-white/70">Топ сейчас: {trendingWords[0][0]}</span>
              )}
            </div>
            <p className="text-sm text-white/70 mb-4">Введите слово, чтобы узнать, сколько раз его произносили в чате pespatron44rus.</p>
            <input
              value={query}
              onChange={e=>setQuery(e.target.value.toLowerCase())}
              placeholder="Например: имба, смешно, спасибо…"
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder-white/40 focus:border-pink-400 focus:ring-2 focus:ring-pink-500/40 outline-none transition"
            />
            <div
              className={`mt-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/90 min-h-[64px] flex flex-col justify-center transition ${searchHasData ? 'cursor-pointer hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-pink-500/60' : ''}`}
              role={searchHasData ? 'button' : undefined}
              tabIndex={searchHasData ? 0 : undefined}
              onClick={() => searchHasData && handleWordClick(query)}
              onKeyDown={e => {
                if (searchHasData && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleWordClick(query);
                }
              }}
            >
              {query
                ? wordCounts[query]
                  ? <>
                      Слово «{query}» использовано {wordCounts[query]} раз
                      <span className="text-xs text-white/60 mt-1">Нажмите, чтобы открыть график</span>
                    </>
                  : `Слово «${query}» пока не встречалось`
                : 'Введите слово и получите статистику мгновенно'}
            </div>
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">🔥 Сейчас в топе</p>
              <div className="mt-3 flex flex-col gap-2">
                {trendingWords.map(([w,c],i)=>(
                  <button
                    type="button"
                    key={w}
                    onClick={() => handleWordClick(w)}
                    className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-left hover:bg-white/10 transition focus-visible:ring-2 focus-visible:ring-pink-500/60"
                  >
                    <span className="text-white/90">{i+1}. {w}</span>
                    <span className="text-white/60">{c}</span>
                  </button>
                ))}
                {trendingWords.length === 0 && <p className="text-white/50 text-sm">Загружаем…</p>}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-black/60 to-black/40 border border-white/10 rounded-3xl p-6 shadow-2xl text-left">
            <h3 className="text-xl font-semibold mb-4">🏆 Топ-100 слов</h3>
            <div
              className="space-y-2 max-h-[520px] overflow-y-auto pr-2 hide-scrollbar"
              style={{ msOverflowStyle: 'none' }}
            >
              {topWords.map(([w,c],i)=>(
                <button
                  type="button"
                  key={w}
                  onClick={() => handleWordClick(w)}
                  className="flex items-center justify-between rounded-2xl bg-white/5 border border-white/10 px-4 py-2 text-left w-full hover:bg-white/10 transition cursor-pointer focus-visible:ring-2 focus-visible:ring-pink-500/60"
                >
                  <span className="text-white/90 text-sm">{i+1}. {w}</span>
                  <span className="text-white/60 text-sm">{c}</span>
                </button>
              ))}
              {topWords.length === 0 && <p className="text-white/50 text-sm">Данные загружаются…</p>}
            </div>
          </div>

          <div className="bg-gradient-to-br from-black/60 to-black/45 border border-white/10 rounded-3xl p-6 shadow-2xl text-left">
            <h3 className="text-xl font-semibold mb-4">👑 Топ-15 активных</h3>
            <div
              className="space-y-2 max-h-[520px] overflow-y-auto pr-1 hide-scrollbar"
              style={{ msOverflowStyle: 'none' }}
            >
              {topUsers.map(([u,c],i)=>(
                <div key={u} className="flex items-center justify-between rounded-2xl bg-white/5 border border-white/10 px-4 py-2">
                  <span className="text-white/90 text-sm">{i+1}. {u}</span>
                  <span className="text-white/60 text-sm">{c}</span>
                </div>
              ))}
              {topUsers.length === 0 && <p className="text-white/50 text-sm">Ждём сообщения…</p>}
            </div>
          </div>
        </div>
      </div>

      {selectedWordData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeWordModal} />
          <div className="relative z-10 w-full max-w-2xl bg-gradient-to-br from-[#0f0f1f] via-[#141428] to-[#1c1c3a] border border-white/10 rounded-3xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto hide-scrollbar">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Слово</p>
                <h2 className="text-3xl font-bold text-white">{selectedWord}</h2>
                <p className="text-sm text-white/70 mt-1">Всего упоминаний: {selectedWordTotal}</p>
              </div>
              <button
                type="button"
                onClick={closeWordModal}
                className="w-10 h-10 rounded-2xl bg-white/10 text-white text-xl hover:bg-white/20 transition"
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.3em] text-white/50">Топ авторов</p>
              {topContributor && (
                <p className="mt-2 text-sm text-white/70">
                  Чаще всего писал: <span className="text-white font-semibold">{topContributor[0]}</span> — {topContributor[1]}
                </p>
              )}
              <div className="mt-4 space-y-3">
                {topContributors.length > 0 ? (
                  topContributors.map(([user, count], idx) => {
                    const percentage = ((count / contributorBase) * 100);
                    const percentageLabel = percentage.toFixed(1);
                    return (
                      <div key={user} className="bg-white/5 border border-white/10 rounded-2xl p-3">
                        <div className="flex items-center justify-between text-sm text-white/80 mb-2">
                          <span>{idx + 1}. {user}</span>
                          <span>{count} ({percentageLabel}%)</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"
                            style={{ width: `${Math.min(100, percentage)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-white/60 text-sm">Недостаточно данных по авторам.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatPage;
